/**
 * Ellie Minibot — Multi-Product Bundle Picker (Dawn theme build)
 * ---------------------------------------------------
 * Built specifically against Dawn's known cart implementation, not guessed:
 *   - Header cart count: <a id="cart-icon-bubble"> rendered by the built-in
 *     `sections/cart-icon-bubble.liquid` section.
 *   - Cart drawer: <cart-drawer id="CartDrawer"> custom element rendered by
 *     `sections/cart-drawer.liquid`, containing a `.drawer__inner` wrapper
 *     that Dawn itself replaces the innerHTML of on cart updates.
 *   - Dawn's own product-form.js adds to cart by POSTing to /cart/add.js
 *     with a `sections` param so the response includes fresh HTML for those
 *     sections in the same round trip — this script does the same thing,
 *     which is the pattern Dawn is actually built to support (rather than a
 *     second, separate re-fetch after the add).
 *
 * One section can hold multiple instances on a page (unlikely, but supported).
 * Each instance reads its own JSON data blob and wires up:
 *   - row click -> swap image/price/specs/fulfillment note
 *   - "Add to cart" -> single-variant add
 *   - "Add Complete Set" -> multi-line-item add for every block marked
 *     includeInBundle: true
 *
 * Notes:
 *  - This does NOT apply the bundle discount itself. Pair it with an
 *    automatic discount (keyed on bundleTag or on the set of variant IDs)
 *    or a bundle app.
 *  - Availability is read from the variant snapshot at page render time.
 *    For a drop with fast-moving in-house inventory, consider re-checking
 *    via a lightweight /products/{handle}.js fetch before the bundle add,
 *    since the page itself may be cached.
 */

(function () {
  'use strict';

  // Dawn's built-in section IDs for the header cart bubble and the cart
  // drawer. These are stable across unmodified Dawn installs (they're the
  // theme's own default section filenames), unlike the earlier custom-theme
  // guesswork this script used to need.
  const CART_ICON_SECTION_ID = 'cart-icon-bubble';
  const CART_DRAWER_SECTION_ID = 'cart-drawer';

  function updateCartIconBubble(sectionsHtml) {
    const markup = sectionsHtml && sectionsHtml[CART_ICON_SECTION_ID];
    if (!markup) {
      console.warn('Minibot bundle: no "' + CART_ICON_SECTION_ID + '" markup in cart response — check that this section file still exists under that name in the theme.');
      return;
    }

    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const newBubble = parsed.getElementById('cart-icon-bubble');
    const oldBubble = document.getElementById('cart-icon-bubble');

    if (!oldBubble) {
      console.warn('Minibot bundle: no element with id="cart-icon-bubble" found on this page — the header markup may not match stock Dawn. Falling back to a generic /cart.js count patch.');
      return;
    }
    if (newBubble) {
      oldBubble.replaceWith(newBubble);
    }
  }

  function updateCartDrawer(sectionsHtml) {
    const markup = sectionsHtml && sectionsHtml[CART_DRAWER_SECTION_ID];
    if (!markup) {
      console.warn('Minibot bundle: no "' + CART_DRAWER_SECTION_ID + '" markup in cart response — check that this section file still exists under that name in the theme.');
      return;
    }

    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const newInner = parsed.querySelector('cart-drawer .drawer__inner') || parsed.querySelector('.drawer__inner');
    const oldInner = document.querySelector('cart-drawer .drawer__inner');

    if (!oldInner) {
      console.warn('Minibot bundle: no ".drawer__inner" found inside <cart-drawer> on this page — the drawer markup may not match stock Dawn.');
      return;
    }
    if (newInner) {
      oldInner.innerHTML = newInner.innerHTML;
      oldInner.classList.remove('is-empty');
    }
  }

  // Belt-and-suspenders: regardless of whether the section-based update above
  // found matching markup, directly patch any common cart-count element with
  // the real count from /cart.js. This covers themes/forks where the count
  // element exists but under slightly different structure than stock Dawn.
  async function patchCartCountFallback() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      const selectors = [
        '#cart-icon-bubble .cart-count-bubble span[aria-hidden="true"]',
        '.cart-count-bubble span',
        '.cart-count-bubble',
        '[data-cart-count]'
      ];
      let patched = 0;
      selectors.forEach((selector) => {
        document.querySelectorAll(selector).forEach((el) => {
          el.textContent = cart.item_count;
          patched++;
        });
      });
      console.log('Minibot bundle: /cart.js fallback patched ' + patched + ' element(s) with item_count=' + cart.item_count + '. If your header count still looks stale, inspect it in DevTools and tell me its exact id/class so I can target it directly.');
    } catch (err) {
      console.error('Minibot bundle: /cart.js fallback fetch failed', err);
    }
  }

  function openCartDrawer() {
    const drawer = document.querySelector('cart-drawer');
    // Dawn's CartDrawer custom element exposes an .open() method.
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    }
  }

  function initSection(root) {
    const sectionId = root.dataset.sectionId;
    const dataScript = document.getElementById('minibot-bundle-data-' + sectionId);
    if (!dataScript) return;

    let data;
    try {
      data = JSON.parse(dataScript.textContent);
    } catch (e) {
      console.error('Minibot bundle: could not parse item data', e);
      return;
    }

    const items = data.items || [];
    if (!items.length) {
      console.warn(
        'Minibot bundle (' + sectionId + '): no items found. ' +
        'Check that every block in the theme editor has a real product assigned — ' +
        'blocks with no product selected are skipped entirely, and buttons won\'t work if the list is empty.'
      );
      return;
    }

    const unavailableCount = items.filter((i) => !i.available).length;
    if (unavailableCount) {
      console.info(
        'Minibot bundle (' + sectionId + '): ' + unavailableCount + ' of ' + items.length +
        ' item(s) are showing as unavailable. This can also happen if the assigned product has ' +
        'no published/active variant, not just true sold-out stock.'
      );
    }

    const mainImage = document.getElementById('minibot-bundle-main-image-' + sectionId);
    const priceEl = document.getElementById('minibot-bundle-price-' + sectionId);
    const specsEl = document.getElementById('minibot-bundle-specs-' + sectionId);
    const fulfillmentEl = document.getElementById('minibot-bundle-fulfillment-' + sectionId);
    const sizePickerEl = document.getElementById('minibot-bundle-sizes-' + sectionId);
    const bundleSizesEl = document.getElementById('minibot-bundle-bundle-sizes-' + sectionId);
    const errorEl = document.getElementById('minibot-bundle-error-' + sectionId);
    const addSingleBtn = document.getElementById('minibot-bundle-add-single-' + sectionId);
    const addSetBtn = document.getElementById('minibot-bundle-add-set-' + sectionId);
    const thumbButtons = root.querySelectorAll('.minibot-bundle__thumb');

    let selectedIndex = 0;
    // Tracks the chosen size variant per item index, e.g. { 0: {id, size, price, ...} }.
    // Keyed by item index (not item title) so the t-shirt and sweatshirt can each
    // hold their own independent size choice at the same time. Shared between the
    // single-item picker and the bundle panel's pickers below.
    const selectedVariantByItem = {};

    const fulfillmentCopy = {
      printify: 'Made to order — ships separately from in-house items.',
      in_house: 'In stock and ships from our warehouse.',
      digital: 'Digital item — delivered by email, no shipping.'
    };

    // Returns the variant that would actually be added to cart for this item
    // right now: the chosen size if one's required and picked, the item's
    // single/default variant if no size choice applies, or null if a size is
    // required but hasn't been chosen yet.
    function effectiveVariant(index) {
      const item = items[index];
      if (!item) return null;
      if (item.requiresSize) {
        return selectedVariantByItem[index] || null;
      }
      return {
        id: item.variantId,
        priceFormatted: item.priceFormatted,
        available: item.available
      };
    }

    function sizePillsMarkup(index) {
      const item = items[index];
      if (!item || !item.variants) return '';
      const chosen = selectedVariantByItem[index];

      return item.variants
        .map((v) => {
          const isSelected = chosen && chosen.id === v.id;
          const classes = 'minibot-bundle__size-pill' + (isSelected ? ' is-selected' : '');
          const disabledAttr = v.available ? '' : 'disabled';
          return (
            '<button type="button" class="' + classes + '" data-item-index="' + index +
            '" data-variant-id="' + v.id + '" ' + disabledAttr + '>' +
            v.size +
            '</button>'
          );
        })
        .join('');
    }

    function renderSizePicker(index) {
      const item = items[index];
      if (!sizePickerEl) return;

      if (!item.requiresSize || !item.variants || item.variants.length <= 1) {
        sizePickerEl.hidden = true;
        sizePickerEl.innerHTML = '';
        return;
      }

      sizePickerEl.hidden = false;
      sizePickerEl.innerHTML =
        '<p class="minibot-bundle__size-label">Select a size — ' + item.title + '</p>' +
        '<div class="minibot-bundle__size-pills">' + sizePillsMarkup(index) + '</div>';
    }

    function renderBundleSizePickers() {
      if (!bundleSizesEl) return;

      const groups = [];
      items.forEach((item, index) => {
        if (!item.includeInBundle || !item.requiresSize || !item.variants || item.variants.length <= 1) return;
        groups.push(
          '<div class="minibot-bundle__bundle-size-group">' +
            '<p class="minibot-bundle__size-label">' + item.title + '</p>' +
            '<div class="minibot-bundle__size-pills">' + sizePillsMarkup(index) + '</div>' +
          '</div>'
        );
      });

      bundleSizesEl.innerHTML = groups.join('');
      bundleSizesEl.hidden = groups.length === 0;

      updateAddSetButtonState();
    }

    function updateAddSetButtonState() {
      if (!addSetBtn) return;

      const missing = [];
      const soldOut = [];
      items.forEach((item, index) => {
        if (!item.includeInBundle) return;
        const variant = effectiveVariant(index);
        if (!variant) {
          missing.push(item.title);
        } else if (!variant.available) {
          soldOut.push(item.title);
        }
      });

      if (soldOut.length) {
        addSetBtn.disabled = true;
        addSetBtn.textContent = 'Sold out';
      } else if (missing.length) {
        addSetBtn.disabled = true;
        addSetBtn.textContent = 'Select sizes to continue';
      } else {
        addSetBtn.disabled = false;
        addSetBtn.textContent = 'Add Complete Set';
      }
    }

    function handleSizePillClick(event) {
      const pill = event.target.closest('.minibot-bundle__size-pill');
      if (!pill || pill.disabled) return;

      const index = Number(pill.dataset.itemIndex);
      const variantId = Number(pill.dataset.variantId);
      const item = items[index];
      const variant = (item.variants || []).find((v) => v.id === variantId);
      if (!variant) return;

      selectedVariantByItem[index] = variant;

      // Refresh both pickers — a size chosen from the bundle panel should also
      // reflect in the single-item picker if that item happens to be selected,
      // and vice versa, since they share the same underlying state.
      renderSizePicker(selectedIndex);
      renderBundleSizePickers();
      updateSelectionDisplay(selectedIndex);
      clearError();
    }

    if (sizePickerEl) {
      sizePickerEl.addEventListener('click', handleSizePillClick);
    }
    if (bundleSizesEl) {
      bundleSizesEl.addEventListener('click', handleSizePillClick);
    }

    function updateSelectionDisplay(index) {
      const item = items[index];
      if (!item) return;

      const variant = effectiveVariant(index);

      if (priceEl) {
        priceEl.textContent = (variant && variant.priceFormatted) || item.priceFormatted;
      }
      if (addSingleBtn) {
        if (item.requiresSize && !variant) {
          addSingleBtn.disabled = true;
          addSingleBtn.textContent = 'Select a size';
        } else {
          const available = variant ? variant.available : item.available;
          addSingleBtn.disabled = !available;
          addSingleBtn.textContent = available ? 'Add to cart' : 'Sold out';
        }
      }
    }

    function render(index) {
      const item = items[index];
      if (!item) return;
      selectedIndex = index;

      if (mainImage && item.image) {
        mainImage.src = item.image;
        mainImage.alt = item.title;
      }
      if (specsEl) {
        specsEl.innerHTML = item.specs || '';
      }
      if (fulfillmentEl) {
        fulfillmentEl.textContent = fulfillmentCopy[item.fulfillment] || '';
      }

      renderSizePicker(index);
      updateSelectionDisplay(index);

      thumbButtons.forEach((btn) => {
        btn.classList.toggle('is-selected', Number(btn.dataset.index) === index);
      });

      clearError();
    }

    function showError(message) {
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.hidden = false;
    }

    function clearError() {
      if (!errorEl) return;
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    async function addToCart(lineItems, button) {
      console.log('Minibot bundle: adding to cart', lineItems);
      clearError();
      const originalLabel = button ? button.textContent : null;
      if (button) {
        button.disabled = true;
        button.textContent = 'Adding…';
      }

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: lineItems,
            sections: [CART_ICON_SECTION_ID, CART_DRAWER_SECTION_ID].join(','),
            sections_url: window.location.pathname
          })
        });

        const result = await response.json();

        if (!response.ok) {
          // Shopify returns { description, message } on error, e.g. insufficient stock
          const message = result.description || result.message || 'Something went wrong adding this to your cart.';
          showError(message);
          return;
        }

        document.dispatchEvent(
          new CustomEvent('minibot-bundle:added', { detail: { result: result } })
        );

        if (result.sections) {
          updateCartIconBubble(result.sections);
          updateCartDrawer(result.sections);
        } else {
          console.warn('Minibot bundle: response had no "sections" key — cart UI cannot be updated in place. Check that this theme is Dawn (or a Dawn-based fork) with the default cart-icon-bubble/cart-drawer sections intact.');
        }

        await patchCartCountFallback();
        openCartDrawer();
      } catch (err) {
        console.error('Minibot bundle: add to cart failed', err);
        showError('Could not reach the cart. Please try again.');
      } finally {
        if (button) {
          button.disabled = false;
          button.textContent = originalLabel;
        }
      }
    }

    thumbButtons.forEach((btn) => {
      btn.addEventListener('click', () => render(Number(btn.dataset.index)));
    });

    if (addSingleBtn) {
      addSingleBtn.addEventListener('click', () => {
        const item = items[selectedIndex];
        if (!item) return;

        const variant = effectiveVariant(selectedIndex);
        if (!variant) {
          showError('Please select a size before adding to cart.');
          return;
        }
        if (!variant.available) return;

        addToCart([{ id: variant.id, quantity: 1 }], addSingleBtn);
      });
    }

    if (addSetBtn) {
      addSetBtn.addEventListener('click', () => {
        const lineItems = [];
        const problems = [];

        items.forEach((item, index) => {
          if (!item.includeInBundle) return;

          const variant = effectiveVariant(index);
          if (!variant) {
            problems.push(item.title + ' — select a size');
            return;
          }
          if (!variant.available) {
            problems.push(item.title + ' — sold out');
            return;
          }
          lineItems.push({ id: variant.id, quantity: 1 });
        });

        if (problems.length) {
          showError('Complete Set unavailable — ' + problems.join(', ') + '.');
          return;
        }

        addToCart(lineItems, addSetBtn);
      });
    }

    // Initial render
    render(0);
    renderBundleSizePickers();
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.minibot-bundle').forEach(initSection);
  });
})();
